import cartesian from "@/utils/cartesian";
import groupRecordsByPinyin from "@/utils/groupRecordsByPinyin";
import { isSpace } from "@/utils/keyBoard";
import { useAsyncEffect, useGetState, useMemoizedFn } from "ahooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
export default function useViewModel(props) {
  const {
    defaultValue,
    suggestTags,
    suggestVisible: sv,
    onChange,
    tags: _tags,
    value,
    allTags,
    disabled,
  } = props;

  const _value = _tags || value;
  const [searchValue, setSearchValue, getSearchValue] = useGetState("");
  const [options, setOptions] = useState([]);
  const [tags, setTags, getTags] = useGetState([
    ...(_value ?? defaultValue ?? []),
  ]);
  const usingInputMethod = useRef(false);
  const isFocused = useRef(false);

  const onFocus = useMemoizedFn(() => {
    isFocused.current = true;
  });
  const onBlur = useMemoizedFn(() => {
    isFocused.current = false;
  });

  const suggestVisible = useMemo(() => sv ?? true, [sv]);
  useAsyncEffect(async () => {
    if (allTags?.length) {
      const groupByPin = await groupRecordsByPinyin(
        allTags,
        (record) => record.tag_name
      );
      let options = [];
      // 将值取出来按a-z排序
      groupByPin.forEach((item) => {
        options = [...options, ...item[1]];
      });
      const pinyinjs = (await import("thepinyin.js")).default;
      options = options.map((item) => {
        // 限制一下5个多音字，不然笛卡尔积会很多
        let count = 0;
        let _pinyin = cartesian(
          (pinyinjs.py(item.tag_name, pinyinjs.NOMARK) || []).map((item) => {
            if (Array.isArray(item)) {
              count++;
              if (count <= 5) {
                return item;
              } else {
                return [item[0]];
              }
            } else {
              return [item];
            }
          })
        );

        return {
          label: item.tag_name,
          value: item.tag_name,
          _pinyin,
        };
      });

      setOptions(options);
    }
  }, [allTags]);

  const onSuggestTagClick = useCallback(
    (e) => {
      if (disabled) return;
      const newTag = e.target.dataset["tag"];
      if (tags.includes(newTag)) return;
      const newTags = [...tags, newTag];
      setTags(newTags);
      onChange && requestAnimationFrame(() => onChange([...newTags]));
    },
    [disabled, tags, setTags, onChange]
  );
  const onInputKeyDown = (e) => {
    // 单独处理下空格的情况，空格时候自动将内容变成标签
    if (isSpace(e) && !usingInputMethod.current) {
      e.preventDefault();
      e.stopPropagation();
      const newTag = getSearchValue();
      setSearchValue("");
      const tags = getTags();
      // 内容为空或已经存在则不进行下一步操作
      if (tags.includes(newTag) || newTag.trim() === "") return;
      tags.push(newTag);
      setTags([...tags]);
      onChange && onChange([...tags]);
    }
  };
  const compositionStartHandler = useMemoizedFn(() => {
    if (!isFocused.current) return;

    usingInputMethod.current = true;
  });
  const compositionEndHandler = useMemoizedFn(() => {
    if (!isFocused.current) return;

    usingInputMethod.current = false;
  });
  const filterOption = (inputValue, option) => {
    // 内容匹配
    const contentMatch = (option?.value ?? "")
      .toLowerCase()
      .includes(inputValue.toLowerCase());
    // 全拼音匹配
    const isPinyinMatch = option?._pinyin
      ?.map((arr) => arr.join(""))
      ?.some((pinyin) => pinyin.includes(inputValue));
    // 首字母匹配
    const isFirstLetterMatch = option?._pinyin
      ?.map((arr) => arr.map((i) => i[0]).join(""))
      ?.some((pinyin) => pinyin.includes(inputValue));
    return contentMatch || isPinyinMatch || isFirstLetterMatch;
  };
  useEffect(() => {
    document.addEventListener("compositionstart", compositionStartHandler);
    document.addEventListener("compositionend", compositionEndHandler);
    return () => {
      document.removeEventListener("compositionstart", compositionStartHandler);
      document.removeEventListener("compositionend", compositionEndHandler);
    };
  }, []);
  const onSelectChange = useCallback(
    (value) => {
      setSearchValue("");

      setTags(value);
      onChange && requestAnimationFrame(() => onChange(value));
    },
    [setSearchValue, setTags, onChange]
  );

  useEffect(() => {
    setTags([...(_value ?? [])]);
  }, [_value, setTags]);

  return {
    ...props,
    options,
    suggestVisible,
    suggestTags,
    tags,
    searchValue,
    onSuggestTagClick,
    onInputKeyDown,
    onSelectChange,
    onSearchChange: setSearchValue,
    onFocus,
    onBlur,
    filterOption,
  };
}
